<template>
  <div class="events-list">
    <HeroSection
      :title="heroDetails.title"
      :description="heroDetails.subTitle"
      :showWhiteWave="heroDetails.showWhiteWave"
      :reverseOrder="true"
      :navIndicator="navIndicator"
      backgroundImage="event-hero-background.svg"
    ></HeroSection>
    <div class="section__container">
      <div class="loader" v-if="UIController.loader.contentLoading">
        <v-progress-circular indeterminate></v-progress-circular>
        <p class="mt-5">Hold On! while we fetch upcoming events for you.</p>
      </div>
      <div v-else>
        <v-card elevation="0" class="error-card" v-if="!hasJobOpenings">
          <v-card-title class="error-card__title text-center dms700"
            >There are no Upcoming Events</v-card-title
          >
        </v-card>
        <div v-else>
          <div
            class="event-item"
            v-for="event in upcomingEvents"
            :key="event.slug"
          >
            <!-- Date with orange dot -->
            <div class="event-date">
              <span class="date-dot"></span>
              <span class="date-text" v-html="eventDateComputed(event.eventDate)"></span>
            </div>
            
            <!-- Event Card -->
            <v-card
              elevation="0"
              class="event-card"
              @click="navigateToEvent(event.slug)"
            >
              <v-card-text class="event-card-content text-left">
                <v-row align="center" no-gutters>
                  <v-col cols="12" md="8" class="event-info">
                    <!-- Event Category -->
                    <v-chip 
                      size="small" 
                      class="event-category-chip"
                      :color="event.eventCategory.color"

                    >
                      {{ event.eventCategory.name }}
                    </v-chip>
                    
                    <!-- Event Title -->
                    <h3 class="event-title">
                      {{ event.name }}
                    </h3>
                    
                    <!-- Location or Virtual -->
                    <div class="event-location">
                      <v-icon 
                        size="small" 
                        class="location-icon"
                        :icon="event.eventMode === 'In-Person' ? 'mdi-map-marker-outline' : 'mdi-monitor'"
                      ></v-icon>
                      <span class="location-text">
                        {{ event.eventMode === 'In-Person' ? event.location : 'Virtual' }}
                      </span>
                    </div>
                  </v-col>
                  
                  <v-col cols="12" md="4" class="event-image-col">
                    <v-img
                      :src="event.featuredImage"
                      cover
                      class="event-image"
                      :lazy-src="require('@/assets/img/lazy-img.jpg')"
                      aspect-ratio="1.777"
                    ></v-img>
                  </v-col>
                </v-row>
              </v-card-text>
            </v-card>
          </div>
        </div>
      </div>
    </div>
    <!-- <v-snackbar
      v-model="UIController.snackbar.active"
      :location="UIController.snackbar.location"
      :text="'UIController.snackbar.text'"
      :color="UIController.snackbar.color"
    >
      {{ UIController.snackbar.text }}</v-snackbar
    > -->
  </div>
</template>
<script setup>
import { computed, onMounted, reactive, ref, nextTick } from 'vue';
import { useRouter } from 'vue-router';
import HeroSection from './HeroSection.vue';

import eventsService from '@/services/API/eventsService';

const router = useRouter();

// Global Variables
const heroDetails = ref({
  title: 'Upcoming Events',
  showWhiteWave: true,
  subTitle: `Explore what's next in learning, sharing, and networking`,
  
});
const navIndicator = ref([
  { text: 'Home', icon: 'fas fa-home', link: '/' },
  { text: 'Upcoming Events', link: '' }
]);

const UIController = reactive({
  loader: {
    contentLoading: true,
  },
  snackbar: {
    active: false,
    location: 'bottom right',
    text: '',
    color: 'danger',
  },
});

// fetched variables
let upcomingEvents = ref([]);

// On Created Function
onMounted(async () => {
  const loaderSection = document.getElementById('loaderSection');
  if (loaderSection) {
    loaderSection.classList.add('d-none');
  }
  
  try {
    const response = await eventsService.listAllEvents();
    
    console.log('API Response:', response);
    
    // Check if response has the expected structure
    if (response && response.data) {
      // If response.data is an array, use it directly
      if (Array.isArray(response.data)) {
        upcomingEvents.value = response.data;
      }
      // If response.data has a data property (like jobOpenings), use that
      else if (response.data.data && Array.isArray(response.data.data)) {
        upcomingEvents.value = response.data.data;
      }
      // If response.data is an object with events array
      else if (response.data.events && Array.isArray(response.data.events)) {
        upcomingEvents.value = response.data.events;
      }
      // Otherwise, try to use response.data directly
      else {
        upcomingEvents.value = [response.data];
      }
      
      UIController.snackbar.text = 'Events fetched successfully';
      UIController.snackbar.color = 'success';
      UIController.snackbar.active = true;
      
      console.log('Upcoming events:', upcomingEvents.value);
    } else {
      console.error('Unexpected response structure:', response);
      upcomingEvents.value = [];
      UIController.snackbar.text = 'No events data received';
      UIController.snackbar.color = 'warning';
      UIController.snackbar.active = true;
    }
  } catch (error) {
    console.error('Error fetching events:', error);
    upcomingEvents.value = [];
    UIController.snackbar.text = 'Could not fetch events';
    UIController.snackbar.color = 'error';
    UIController.snackbar.active = true;
  } finally {
    UIController.loader.contentLoading = false;
  }
});

// Methods
const eventDateComputed = (date) => {
  const eventDate = new Date(parseInt(date));
  const day = eventDate.getDate();
  const month = eventDate.toLocaleDateString('en-US', { month: 'long' });
  const year = eventDate.getFullYear();
  const dayName = eventDate.toLocaleDateString('en-US', { weekday: 'long' });
  const time = eventDate.toLocaleTimeString('en-US', { 
    hour: '2-digit', 
    minute: '2-digit',
    hour12: true 
  });
  
  return `${day} ${month}, ${year}, &nbsp; | &nbsp; ${dayName} &nbsp; | &nbsp; ${time}`;
};

const navigateToEvent = async (slug) => {
  try {
    // Navigate to the event detail page
    await router.push(`/events/${slug}`);
    
    // Scroll to top after navigation
    await nextTick(() => {
      // Multiple scroll methods to ensure compatibility
      window.scrollTo({
        top: 0,
        left: 0,
        behavior: 'smooth'
      });
      
      // Fallback for older browsers
      window.scrollTo(0, 0);
      
      // Also try document body and html scroll
      document.body.scrollTop = 0;
      document.documentElement.scrollTop = 0;
    });
  } catch (error) {
    console.error('Navigation error:', error);
    // Fallback navigation without scroll
    router.push(`/events/${slug}`);
  }
};

// Computed properties
const hasJobOpenings = computed(() => {
  return upcomingEvents.value && upcomingEvents.value.length > 0;
});
</script>
<style scoped>
.events-list {
  background-color: #f8f9fa;
}

.section__container {
  max-width: 1200px;
  margin: 0 auto;
  padding: 2rem 1rem;
  padding-top: 5rem;
}

.error-card {
  background-color: var(--light-1);
  max-width: 500px;
  margin-inline: auto;
  margin-block: 2rem;
}

.loader {
  padding-block: 5rem;
  text-align: center;
}

.event-item {
  margin-bottom: 3rem;
}

.event-date {
  display: flex;
  align-items: center;
  margin-bottom: 1rem;
  font-size: 0.9rem;
  color: #666;
  font-weight: 500;
}

.date-dot {
  width: 8px;
  height: 8px;
  background-color: #ff6b35;
  border-radius: 50%;
  margin-right: 0.75rem;
  flex-shrink: 0;
}

.date-text {
  line-height: 1.2;
}

.event-card {
  background: white;
  border-radius: 12px;
  cursor: pointer;
  transition: all 0.3s ease;
  border: 1px solid #e0e0e0;
}

.event-card:hover {
  transform: translateY(-2px);
  background-color: white !important;
}

.event-card-content {
  padding: 1.5rem;
}

.event-info {
  padding-right: 1rem;
}

.event-category-chip {
  margin-bottom: 1rem;
  font-size: 0.65rem;
  color: #777474;
  letter-spacing: 0.5px;
}

.event-title {
  font-size: 1.5rem;
  font-weight: 600;
  color: #2c3e50;
  line-height: 1.3;
  margin-bottom: 3rem;
}

.event-location {
  display: flex;
  align-items: center;
  color: #666;
  font-size: 0.9rem;
}

.location-icon {
  margin-right: 0.5rem;
  color: #999;
}

.location-text {
  font-weight: 500;
}

.event-image-col {
  display: flex;
  justify-content: left;
  align-items: left;
}

.event-image {
  border-radius: 8px;
  max-width: 400px;
  width: 100%;
  /* max-height: 250px; */
}

/* Mobile responsiveness */
@media (max-width: 960px) {
  .event-info {
    padding-right: 0;
    margin-bottom: 1rem;
  }
  
  .event-title {
    font-size: 1.25rem;
    margin-bottom: 2rem;
  }
  
  /* .event-image {
    max-height: 150px;
  }
   */
  .section__container {
    padding: 3rem 0.5rem;
  }
}

@media (max-width: 600px) {
  .event-title {
    font-size: 1.1rem;
  }
  
  .date-text {
    font-size: 0.8rem;
  }
  
  .event-card-content {
    padding: 1rem;
  }
}
</style>